import React from 'react';
import styled from 'styled-components';
import { FaCheckCircle, FaTimesCircle, FaPlus, FaMinus } from 'react-icons/fa';

// --- Styled Components ---

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

// --- Packages Section ---

const HeaderTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  font-size: 2.5em;
  font-weight: 700;
  color: #333;
`;

const PackagesContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PackageCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 350px;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 1024px) {
    max-width: 80%;
  }

  @media (max-width: 600px) {
    max-width: 100%;
  }
`;

const CardHeader = styled.div`
  background: ${props => props.bgColor || '#ffc107'};
  color: white;
  padding: 20px;
  text-align: center;
`;

const HeaderTitleCard = styled.h2`
  font-size: 1.5em;
  margin-bottom: 5px;
`;

const SubtitleCard = styled.p`
  font-size: 0.9em;
  opacity: 0.9;
  margin-bottom: 15px;
`;

const PriceDisplay = styled.div`
  font-size: 2em;
  font-weight: 700;
`;

const PriceDetails = styled.p`
  font-size: 0.8em;
  margin-top: 5px;
  font-weight: 500;
`;

const ExclusiveTag = styled.p`
  font-size: 0.75em;
  font-weight: 300;
  margin-top: 10px;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 15px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.95em;

  svg {
    margin-right: 10px;
    font-size: 1.1em;
  }

  .check {
    color: #4CAF50; /* Green check */
  }

  .cross {
    color: #F44336; /* Red cross */
  }
`;

const ClientSupport = styled.div`
  margin-bottom: 20px;
`;

const SupportTitle = styled.h4`
  font-size: 1em;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
  color: #555;
`;

const SupportList = styled.ul`
  list-style: none;
  padding: 0;
`;

const SupportItem = styled.li`
  display: flex;
  align-items: center;
  font-size: 0.9em;
  margin-bottom: 5px;
`;

const GetStartedButton = styled.button`
  width: 100%;
  padding: 12px 0;
  background-color: #ff9800; /* Orange button */
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1em;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-top: 10px;

  &:hover {
    background-color: #fb8c00;
  }
`;

const TacLink = styled.a`
  display: block;
  text-align: center;
  font-size: 0.85em;
  color: #999;
  margin-top: 10px;
  text-decoration: none;

  &:hover {
    color: #666;
  }
`;

// --- FAQ Section ---

const FAQHeader = styled.h2`
  text-align: center;
  font-size: 2em;
  font-weight: 700;
  margin-bottom: 40px;
  color: #333;
`;

const FAQList = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 40px;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const FAQColumn = styled.div`
  flex: 1;
`;

const FAQItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #ddd;
  font-size: 1em;
  color: #555;
`;

const FAQIcon = styled.span`
  color: #ff9800;
  font-size: 1.2em;
  cursor: pointer;
  margin-left: 15px;
`;

// --- Footer Section ---

const FooterBar = styled.footer`
  background-color: #333;
  color: white;
  padding: 20px;
  text-align: center;
  margin-top: 80px;
`;

const CompanyName = styled.p`
  font-size: 1.2em;
  font-weight: 700;
  margin-bottom: 5px;
`;

const FollowText = styled.p`
  font-size: 0.9em;
  margin-bottom: 15px;
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const FooterButton = styled.a`
  background-color: #ff9800;
  color: white;
  padding: 8px 15px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.9em;
  font-weight: 500;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #fb8c00;
  }
`;

// --- Data ---

const packagesData = [
    {
        title: "Basic or Small Business",
        subtitle: "SEO Packages",
        priceINR: "12,000 INR",
        priceUSD: "150 USD",
        bgColor: "#ffc107",
        features: [
            { text: "Google Map Integration on website", included: true },
            { text: "Google My Business Page Setup and Optimization", included: true },
            { text: "Local Citations - 10", included: true },
            { text: "Local Classifieds - 10", included: true },
            { text: "Local News Outreach - 5", included: true },
            { text: "Google My Business Posting - 1 Daily", included: true },
            { text: "Business Schema Implementation", included: false }, // Excluded
        ],
    },
    {
        title: "Advance or Mid Size Business",
        subtitle: "SEO Packages",
        priceINR: "16,000 INR",
        priceUSD: "220 USD",
        bgColor: "#ff9800",
        features: [
            { text: "Google Map Integration on website", included: true },
            { text: "Google My Business Page Setup and Optimization", included: true },
            { text: "Local Citations - 15", included: true },
            { text: "Local Classifieds - 15", included: true },
            { text: "Local News Outreach - 10", included: true },
            { text: "Google My Business Posting - 1 Daily", included: true },
            { text: "Business Schema Implementation", included: true },
        ],
    },
    {
        title: "Enterprise or E-Commerce Business",
        subtitle: "SEO Packages",
        priceINR: "24,000 INR",
        priceUSD: "350 USD",
        bgColor: "#f44336",
        features: [
            { text: "Google Map Integration on website", included: true },
            { text: "Google My Business Page Setup and Optimization", included: true },
            { text: "Local Citations - 20", included: true },
            { text: "Local Classifieds - 20", included: true },
            { text: "Local News Outreach - 15", included: true },
            { text: "Google My Business Posting - 1 Daily", included: true },
            { text: "Business Schema Implementation", included: true },
        ],
    },
];

const clientSupport = ["Email", "Chat", "Call"];

const faqItems = [
    "What are custom SEO services, and how do they differ from standard SEO packages?",
    "Why should I choose custom SEO services over pre-packaged services?",
    "How do you determine the right custom SEO strategy for my business?",
    "What specific SEO techniques and tactics are included in custom services?",
    "Can custom SEO services accommodate my budget and business size?",
    "How long does it take to see results with custom SEO services?",
    "Will I receive regular progress reports with custom SEO services?",
    "How do I get started with custom SEO services for my business?",
];

// --- Sub-Components ---

const Package = ({ data }) => (
    <PackageCard>
        <CardHeader bgColor={data.bgColor}>
            <HeaderTitleCard>{data.title}</HeaderTitleCard>
            <SubtitleCard>{data.subtitle}</SubtitleCard>
            <PriceDisplay>{data.priceINR} / {data.priceUSD}</PriceDisplay>
            <PriceDetails>Monthly</PriceDetails>
            <ExclusiveTag>EXCLUSIVE OF ALL TAXES</ExclusiveTag>
        </CardHeader>
        <CardContent>
            <FeatureList>
                {data.features.map((feature, index) => (
                    <FeatureItem key={index}>
                        {feature.included ? (
                            <FaCheckCircle className="check" />
                        ) : (
                            <FaTimesCircle className="cross" />
                        )}
                        {feature.text}
                    </FeatureItem>
                ))}
            </FeatureList>

            <ClientSupport>
                <SupportTitle>CLIENT SUPPORT</SupportTitle>
                <SupportList>
                    {clientSupport.map((item, index) => (
                        <SupportItem key={index}>
                            <FaCheckCircle className="check" />
                            {item}
                        </SupportItem>
                    ))}
                </SupportList>
            </ClientSupport>

            <GetStartedButton>Get Started Now!</GetStartedButton>
            <TacLink href="#">T&C Apply</TacLink>
        </CardContent>
    </PackageCard>
);

const FAQItem = ({ question }) => (
    <FAQItemContainer>
        <span>{question}</span>
        <FAQIcon>
            <FaPlus />
        </FAQIcon>
    </FAQItemContainer>
);

// --- Main Component ---

const LocalSEO4 = () => {
    // Split the FAQ into two columns for the desktop layout
    const midPoint = Math.ceil(faqItems.length / 2);
    const faqColumn1 = faqItems.slice(0, midPoint);
    const faqColumn2 = faqItems.slice(midPoint);

    return (
        <PageContainer>
            {/* Packages Section */}
            <HeaderTitle>Local SEO Packages In India</HeaderTitle>
            <PackagesContainer>
                {packagesData.map((pkg, index) => (
                    <Package key={index} data={pkg} />
                ))}
            </PackagesContainer>
            
            <hr /> 

            {/* FAQ Section */}
            <FAQHeader>Frequently Asked Questions</FAQHeader>
            <FAQList>
                <FAQColumn>
                    {faqColumn1.map((question, index) => (
                        <FAQItem key={`col1-${index}`} question={question} />
                    ))}
                </FAQColumn>
                <FAQColumn>
                    {faqColumn2.map((question, index) => (
                        <FAQItem key={`col2-${index}`} question={question} />
                    ))}
                </FAQColumn>
            </FAQList>

            {/* Footer Section */}
            <FooterBar>
                <CompanyName>SRK Software</CompanyName>
                <FollowText>Stay connected with us on Facebook and Linkedin for the latest updates, insights, and industry news.</FollowText>
                <FooterButtons>
                    <FooterButton href="#" style={{ backgroundColor: '#4267B2' }}>Facebook</FooterButton>
                    <FooterButton href="#" style={{ backgroundColor: '#0077b5' }}>LinkedIn</FooterButton>
                </FooterButtons>
            </FooterBar>
        </PageContainer>
    );
};

export default LocalSEO4;

